import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  toast,
} from '@kmgestao/ui';

import { useRequestContractAdjustment } from './queries.js';

const MIN_CHARS = 10;
const MAX_CHARS = 4_000;

/** Modal que coleta texto livre do lead descrevendo o ajuste que ele
 * quer no contrato antes de aprovar. Chama edge function
 * `request-contract-adjustment` que persiste no histórico + notifica
 * Karla por email. Usado em ContractApprovalSection. */
export function AdjustmentRequestDialog({
  open,
  onOpenChange,
  token,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  token: string;
  onSuccess?: () => void;
}) {
  const [text, setText] = useState('');
  const mutation = useRequestContractAdjustment();

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (trimmed.length < MIN_CHARS) {
      toast.error(`Mínimo de ${MIN_CHARS} caracteres.`);
      return;
    }
    try {
      await mutation.mutateAsync({ token, adjustment_text: trimmed });
      toast.success('Ajuste enviado', {
        description: 'A consultora vai revisar e te retornar em breve.',
      });
      setText('');
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error('Não foi possível enviar o ajuste', {
        description: err instanceof Error ? err.message : 'erro desconhecido',
      });
    }
  };

  const chars = text.length;
  const remaining = MAX_CHARS - chars;
  const tooShort = chars > 0 && chars < MIN_CHARS;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Solicitar ajuste no contrato</DialogTitle>
          <DialogDescription>
            Descreva com clareza o que você gostaria de mudar. A consultora revisa o texto do
            contrato e te reenvia com as alterações — depois disso, você pode aprovar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="adjustment-text">O que precisa ser ajustado?</Label>
          <textarea
            id="adjustment-text"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS))}
            disabled={mutation.isPending}
            rows={8}
            placeholder={
              'Ex.: Gostaria de renegociar o prazo de vigência para 24 meses. Também preciso adicionar uma cláusula específica sobre suporte 24/7 — tenho clínica aberta aos sábados.'
            }
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          />
          <div className="flex items-center justify-between text-xs">
            <span className={tooShort ? 'text-destructive' : 'text-muted-foreground'}>
              {tooShort
                ? `Mínimo ${MIN_CHARS} caracteres (faltam ${MIN_CHARS - chars}).`
                : `${chars} caracteres`}
            </span>
            <span className="text-muted-foreground">{remaining} restantes</span>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={mutation.isPending || chars < MIN_CHARS}
          >
            {mutation.isPending ? 'Enviando…' : 'Enviar solicitação'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
